import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Inject, Injectable } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i1 from '@azure/msal-angular';
import { MsalInterceptor, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService, MsalModule as MsalModule$1, MsalRedirectComponent } from '@azure/msal-angular';
export { MsalBroadcastService, MsalGuard, MsalService } from '@azure/msal-angular';
import { InteractionType, MSALInstanceFactory } from '@pichincha/auth';
export { InteractionType } from '@pichincha/auth';
import { InteractionStatus, EventType } from '@azure/msal-browser';
import { Subject, BehaviorSubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

const AzureConfig = new InjectionToken('azureConfig');
const AzureGraphURL = 'https://graph.microsoft.com/v1.0/me';

function MSALInterceptorConfigFactory(azureConfig) {
    const protectedResourceMap = new Map();
    protectedResourceMap.set(AzureGraphURL, azureConfig.azure_scope);
    if (azureConfig.api_url) {
        protectedResourceMap.set(azureConfig.api_url, azureConfig.api_scope);
    }
    return {
        interactionType: azureConfig.interactionType,
        protectedResourceMap,
    };
}
function MSALGuardConfigFactory(azureConfig) {
    return {
        interactionType: azureConfig.interactionType,
        authRequest: {
            scopes: [
                ...azureConfig.azure_scope,
                ...azureConfig.api_scope,
            ],
        },
    };
}
const defaultAzureConfig = {
    secretKey: '',
    client_id: '',
    tenant_id: '',
    interactionType: InteractionType.Redirect,
    redirect_url: '',
    azure_scope: [],
    api_url: '',
    api_scope: [],
};
class MsalModule {
    static forRoot(azureConfig) {
        return {
            ngModule: MsalModule,
            providers: [
                {
                    provide: AzureConfig,
                    useValue: azureConfig,
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MsalInterceptor,
                    multi: true,
                },
                {
                    provide: MSAL_INSTANCE,
                    useFactory: MSALInstanceFactory,
                    deps: [AzureConfig],
                },
                {
                    provide: MSAL_GUARD_CONFIG,
                    useFactory: MSALGuardConfigFactory,
                    deps: [AzureConfig],
                },
                {
                    provide: MSAL_INTERCEPTOR_CONFIG,
                    useFactory: MSALInterceptorConfigFactory,
                    deps: [AzureConfig],
                },
                MsalService,
                MsalGuard,
                MsalBroadcastService,
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MsalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: MsalModule, bootstrap: [MsalRedirectComponent], imports: [MsalModule$1] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MsalModule, imports: [MsalModule$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MsalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MsalModule$1],
                    bootstrap: [MsalRedirectComponent],
                }]
        }] });

class MsalAuthService {
    constructor(authConfig, authService, msalBroadcastService) {
        this.authConfig = authConfig;
        this.authService = authService;
        this.msalBroadcastService = msalBroadcastService;
        this._destroying$ = new Subject();
        this.accountState$ = new BehaviorSubject(false);
        this.interactionState$ = new BehaviorSubject(InteractionStatus.None);
    }
    get msalService() {
        return this.authService;
    }
    initialize(enableObservables) {
        this.authService.initialize().subscribe();
        this.authService.handleRedirectObservable().subscribe();
        this.msalBroadcastService.msalSubject$
            .pipe(filter((msg) => msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED))
            .subscribe(() => {
            if (enableObservables) {
                this.accountState$.next(this.isAuthenticated());
                return;
            }
            const pathname = this.isAuthenticated()
                ? this.authConfig.redirect_url
                : '/';
            window.location.pathname = pathname;
        });
        this.msalBroadcastService.inProgress$
            .pipe(filter((status) => status === InteractionStatus.None), takeUntil(this._destroying$))
            .subscribe(() => {
            if (enableObservables) {
                this.interactionState$.next(InteractionStatus.None);
                return;
            }
            this.checkAndSetActiveAccount();
        });
    }
    checkAndSetActiveAccount() {
        let activeAccount = this.authService.instance.getActiveAccount();
        if (!activeAccount && this.isAuthenticated()) {
            let accounts = this.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
            window.location.pathname = this.authConfig.redirect_url;
        }
    }
    initializeAuth() {
        return this.authService.initialize();
    }
    handleAuthRedirect() {
        return this.authService.handleRedirectObservable();
    }
    getAllAccounts() {
        return this.authService.instance.getAllAccounts();
    }
    authenticate() {
        if (this.authConfig.interactionType === InteractionType.Popup) {
            let arg = this.authConfig.authRequest
                ? { ...this.authConfig.authRequest }
                : undefined;
            this.authService
                .loginPopup(arg)
                .subscribe((response) => {
                this.authService.instance.setActiveAccount(response.account);
            });
            return;
        }
        let argRedirect = this.authConfig.authRequest
            ? { ...this.authConfig.authRequest }
            : undefined;
        this.authService.loginRedirect(argRedirect);
    }
    me() {
        const accounts = this.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }
    async getTokenSilent() {
        return this.authService.instance.acquireTokenSilent({
            account: this.authService.instance.getActiveAccount(),
            scopes: ['user.read'],
        });
    }
    async getToken() {
        const response = await this.getTokenSilent();
        return response.idToken;
    }
    isAuthenticated() {
        return this.getAllAccounts().length > 0;
    }
    async isExpired() {
        const response = await this.getTokenSilent();
        const { expiresOn } = response;
        if (!expiresOn) {
            return true;
        }
        return new Date() >= expiresOn;
    }
    clean() {
        if (this.authConfig.interactionType === InteractionType.Popup) {
            this.authService.logoutPopup({
                mainWindowRedirectUri: '/',
            });
        }
        else {
            this.authService.logoutRedirect();
        }
    }
    getAccountState() {
        return this.accountState$.asObservable();
    }
    getInteractionState() {
        return this.interactionState$.asObservable();
    }
    destroy() {
        this._destroying$.next();
        this._destroying$.complete();
        this.accountState$.complete();
        this.interactionState$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MsalAuthService, deps: [{ token: AzureConfig }, { token: i1.MsalService }, { token: i1.MsalBroadcastService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MsalAuthService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MsalAuthService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AzureConfig]
                }] }, { type: i1.MsalService }, { type: i1.MsalBroadcastService }] });

class AuthService extends MsalAuthService {
}
function factoryFnAuth(authConfig, msalService, msalBroadcastService) {
    return new MsalAuthService(authConfig, msalService, msalBroadcastService);
}
class AuthModule {
    static forRoot(authConfig) {
        const msalCredModule = MsalModule.forRoot(authConfig);
        const providers = msalCredModule.providers;
        return {
            ngModule: AuthModule,
            providers: [
                ...providers,
                {
                    provide: AzureConfig,
                    useValue: authConfig,
                },
                {
                    provide: AuthService,
                    useFactory: factoryFnAuth,
                    deps: [AzureConfig, MsalService, MsalBroadcastService],
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: AuthModule, imports: [MsalModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AuthModule, imports: [MsalModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MsalModule],
                    providers: [],
                }]
        }] });

var EGrantType;
(function (EGrantType) {
    EGrantType["AUTHORIZATION_CODE"] = "authorization_code";
    EGrantType["CLIENT_CREDENTIALS"] = "client_credentials";
    EGrantType["IMPLICIT"] = "implicit";
    EGrantType["PASSWORD"] = "password";
})(EGrantType || (EGrantType = {}));
var EAuthorizationType;
(function (EAuthorizationType) {
    EAuthorizationType["OAUTH2"] = "oauth2";
    EAuthorizationType["AZURE"] = "azure";
    EAuthorizationType["AWS"] = "awsCognito";
    EAuthorizationType["ALTEMISTA"] = "altemista";
})(EAuthorizationType || (EAuthorizationType = {}));
var EAuthorizationProvider;
(function (EAuthorizationProvider) {
    EAuthorizationProvider["GOOGLE"] = "google";
    EAuthorizationProvider["FACEBOOK"] = "facebook";
    EAuthorizationProvider["TWITTER"] = "twitter";
})(EAuthorizationProvider || (EAuthorizationProvider = {}));
var EErrorResponseType;
(function (EErrorResponseType) {
    EErrorResponseType["INVALID_REQUEST"] = "invalid_request";
    EErrorResponseType["INVALID_GRANT"] = "invalid_grant";
    EErrorResponseType["UNAUTHORIZED_CLIENT"] = "unauthorized_client";
    EErrorResponseType["UNSUPPORTED_GRANT_TYPE"] = "unsupported_grant_type";
    EErrorResponseType["INVALID_SCOPE"] = "invalid_scope";
})(EErrorResponseType || (EErrorResponseType = {}));
var EStorageType;
(function (EStorageType) {
    EStorageType["LOCAL"] = "local";
    EStorageType["SESSION"] = "session";
    EStorageType["MEMORY"] = "memory";
})(EStorageType || (EStorageType = {}));
var ERequestType;
(function (ERequestType) {
    ERequestType["JSON"] = "application/json";
    ERequestType["FORM"] = "application/x-www-form-urlencoded";
})(ERequestType || (ERequestType = {}));
var EResponseType;
(function (EResponseType) {
    EResponseType["JSON"] = "application/json";
    EResponseType["XML"] = "application/xml";
    EResponseType["TEXT"] = "text/plain";
})(EResponseType || (EResponseType = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { AuthModule, AuthService, EAuthorizationProvider, EAuthorizationType, EErrorResponseType, EGrantType, ERequestType, EResponseType, EStorageType, MsalModule, factoryFnAuth };
